import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications`, { params: { limit: 30 } });
      setItems(res.data?.items || []);
      setUnread(res.data?.unread_count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 60000);
    return () => clearInterval(id);
  }, [fetchAll]);

  useEffect(() => {
    const onClick = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const markOne = async (id) => {
    try { await axios.post(`${API}/admin/notifications/${id}/mark-read`); fetchAll(); }
    catch { /* silent */ }
  };
  const markAll = async () => {
    try { await axios.post(`${API}/admin/notifications/mark-all-read`); fetchAll(); }
    catch { /* silent */ }
  };

  const onClickItem = (n) => {
    if (!n.read) markOne(n.id);
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Button variant="ghost" size="icon" onClick={() => setOpen((o) => !o)} className="relative h-9 w-9 rounded-sm" data-testid="notification-bell">
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <Badge className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] rounded-full bg-primary text-primary-foreground border-0">{unread > 99 ? '99+' : unread}</Badge>
        )}
      </Button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-card border border-border rounded-sm shadow-xl z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between p-3 border-b border-border">
            <p className="text-xs uppercase tracking-wider font-medium">Notifications</p>
            {unread > 0 && (
              <button onClick={markAll} className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="mark-all-read-btn">
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">No notifications</p>
            ) : (
              items.map((n) => (
                <button key={n.id} onClick={() => onClickItem(n)} className={cn('w-full text-left px-3 py-2 hover:bg-muted/30 border-b border-border last:border-b-0', !n.read && 'bg-primary/5')}>
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{n.title}</p>
                      {n.body && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{n.body}</p>}
                      <p className="text-[10px] text-muted-foreground mt-1">{fmtAgo(n.created_at)} ago</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
